import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar, Clock, Edit3, Plus, Play, Trash2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createRoutine,
  deleteRoutine,
  listRoutineRuns,
  listRoutines,
  listSkills,
  updateRoutine,
  type RoutinePatch,
  type RoutineRow,
  type SkillSummary,
} from "@/lib/agenticClient";

// ─── Cron preset library ────────────────────────────────────────────────────
const CRON_PRESETS: Array<{
  label: string;
  expr: string;
  description: string;
}> = [
  {
    label: "Tous les jours à 8h",
    expr: "0 8 * * *",
    description: "Quotidien matin",
  },
  {
    label: "Jours ouvrés à 8h",
    expr: "0 8 * * 1-5",
    description: "Lundi → vendredi 8h",
  },
  { label: "Lundi à 9h", expr: "0 9 * * 1", description: "Hebdomadaire" },
  {
    label: "Toutes les heures",
    expr: "0 * * * *",
    description: "À chaque heure pile",
  },
  {
    label: "Toutes les 2h (ouvrés)",
    expr: "0 */2 * * 1-5",
    description: "Triage actif",
  },
  { label: "1er du mois à 8h", expr: "0 8 1 * *", description: "Mensuel" },
  { label: "Personnalisé", expr: "", description: "Saisis ton expression" },
];

// ─── Component ──────────────────────────────────────────────────────────────
export function RoutinesPanel() {
  const qc = useQueryClient();
  const { data: routines = [], isLoading } = useQuery({
    queryKey: ["agentic_routines"],
    queryFn: listRoutines,
    refetchInterval: 60_000,
  });
  const { data: skills = [] } = useQuery({
    queryKey: ["agentic_skills"],
    queryFn: listSkills,
  });
  const [editing, setEditing] = useState<RoutineRow | "new" | null>(null);
  const [openRunsFor, setOpenRunsFor] = useState<RoutineRow | null>(null);

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["agentic_routines"] });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <p className="text-sm text-muted-foreground flex-1">
          Routines récurrentes : un skill, un cron, des variables. À chaque
          tick, le scheduler enfile une exécution si l'horaire est passé. Les
          variables{" "}
          <code className="text-xs bg-muted px-1 rounded">{`{{today}}`}</code>,{" "}
          <code className="text-xs bg-muted px-1 rounded">
            {`{{week_start}}`}
          </code>
          ,{" "}
          <code className="text-xs bg-muted px-1 rounded">
            {`{{owner_id}}`}
          </code>{" "}
          sont interpolées au runtime.
        </p>
        <Button onClick={() => setEditing("new")}>
          <Plus className="h-4 w-4 mr-1.5" />
          Nouvelle routine
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Chargement…
          </CardContent>
        </Card>
      ) : routines.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground italic">
            Aucune routine pour l'instant. Clique « Nouvelle routine ».
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {routines.map((r) => (
            <RoutineCard
              key={r.id}
              routine={r}
              onEdit={() => setEditing(r)}
              onDeleted={refresh}
              onToggled={refresh}
              onSeeRuns={() => setOpenRunsFor(r)}
            />
          ))}
        </div>
      )}

      {editing && (
        <RoutineFormDialog
          initial={editing === "new" ? null : editing}
          skills={skills}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}

      {openRunsFor && (
        <RoutineRunsDialog
          routine={openRunsFor}
          onClose={() => setOpenRunsFor(null)}
        />
      )}
    </div>
  );
}

function RoutineCard({
  routine,
  onEdit,
  onDeleted,
  onToggled,
  onSeeRuns,
}: {
  routine: RoutineRow;
  onEdit: () => void;
  onDeleted: () => void;
  onToggled: () => void;
  onSeeRuns: () => void;
}) {
  const del = useMutation({
    mutationFn: () => deleteRoutine(routine.id),
    onSuccess: onDeleted,
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => updateRoutine(routine.id, { enabled }),
    onSuccess: onToggled,
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start gap-2">
          <Calendar className="h-4 w-4 mt-0.5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm break-words">
              {routine.name}
            </CardTitle>
            <CardDescription className="text-xs mt-0.5 font-mono">
              {routine.skill_id} · {routine.cron_expr}
            </CardDescription>
          </div>
          <Switch
            checked={routine.enabled}
            disabled={toggle.isPending}
            onCheckedChange={(v) => toggle.mutate(v)}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <Clock className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">Prochain :</span>
          <span>{formatDate(routine.next_run_at)}</span>
        </div>
        {routine.last_run_at && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Dernier :</span>
            <span>{formatDate(routine.last_run_at)}</span>
            {routine.last_run_status && (
              <Badge
                variant={
                  routine.last_run_status === "done"
                    ? "secondary"
                    : "destructive"
                }
                className="text-[10px]"
              >
                {routine.last_run_status}
              </Badge>
            )}
          </div>
        )}
        {routine.last_run_error && (
          <p className="text-[11px] text-destructive line-clamp-2">
            {routine.last_run_error}
          </p>
        )}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Edit3 className="h-3.5 w-3.5 mr-1.5" />
            Éditer
          </Button>
          <Button variant="ghost" size="sm" onClick={onSeeRuns}>
            <Play className="h-3.5 w-3.5 mr-1.5" />
            Historique
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`Supprimer "${routine.name}" ?`)) del.mutate();
            }}
            disabled={del.isPending}
            className="ml-auto"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

type FormState = {
  name: string;
  skill_id: string;
  cron_expr: string;
  timezone: string;
  variables_text: string;
  enabled: boolean;
};

const DEFAULT_FORM: FormState = {
  name: "",
  skill_id: "",
  cron_expr: "0 8 * * 1-5",
  timezone: "Europe/Paris",
  variables_text: "{}",
  enabled: true,
};

function RoutineFormDialog({
  initial,
  skills,
  onClose,
  onSaved,
}: {
  initial: RoutineRow | null;
  skills: SkillSummary[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(() =>
    initial
      ? {
          name: initial.name,
          skill_id: initial.skill_id,
          cron_expr: initial.cron_expr,
          timezone: initial.timezone,
          variables_text: JSON.stringify(initial.variables, null, 2),
          enabled: initial.enabled,
        }
      : DEFAULT_FORM,
  );
  const [error, setError] = useState<string | null>(null);

  const sortedSkills = useMemo(
    () => [...skills].sort((a, b) => a.id.localeCompare(b.id)),
    [skills],
  );

  const save = useMutation({
    mutationFn: async () => {
      let vars: Record<string, unknown> = {};
      const trimmed = form.variables_text.trim();
      if (trimmed) {
        try {
          vars = JSON.parse(trimmed);
        } catch (e) {
          throw new Error(
            `variables JSON invalide : ${e instanceof Error ? e.message : String(e)}`,
          );
        }
      }
      const body: RoutinePatch = {
        name: form.name.trim(),
        skill_id: form.skill_id,
        cron_expr: form.cron_expr.trim(),
        timezone: form.timezone,
        variables: vars,
        enabled: form.enabled,
      };
      if (!body.name) throw new Error("name requis");
      if (!body.skill_id) throw new Error("skill_id requis");
      if (!body.cron_expr) throw new Error("cron_expr requis");
      if (initial) return updateRoutine(initial.id, body);
      return createRoutine(
        body as RoutinePatch & {
          name: string;
          skill_id: string;
          cron_expr: string;
        },
      );
    },
    onSuccess: onSaved,
    onError: (e) => setError((e as Error).message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initial ? `Éditer "${initial.name}"` : "Nouvelle routine"}
          </DialogTitle>
          <DialogDescription>
            Choisis un skill, un horaire (cron) et les variables passées en
            entrée.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nom">
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Brief matinal"
            />
          </Field>
          <Field label="Skill">
            <Select
              value={form.skill_id}
              onValueChange={(v) => setForm((f) => ({ ...f, skill_id: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisis un skill" />
              </SelectTrigger>
              <SelectContent>
                {sortedSkills.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="font-mono text-xs">{s.id}</span>
                    {s.source === "custom" && (
                      <Badge variant="outline" className="ml-2 text-[9px]">
                        custom
                      </Badge>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Préréglage cron (optionnel)">
          <Select
            value={
              CRON_PRESETS.find((p) => p.expr === form.cron_expr)?.expr ?? ""
            }
            onValueChange={(v) => {
              if (!v) return;
              setForm((f) => ({ ...f, cron_expr: v }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionne un horaire" />
            </SelectTrigger>
            <SelectContent>
              {CRON_PRESETS.map((p) => (
                <SelectItem key={p.label} value={p.expr || "custom"}>
                  <span>{p.label}</span>
                  <span className="text-[11px] text-muted-foreground ml-2">
                    {p.description}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Expression cron">
            <Input
              value={form.cron_expr}
              onChange={(e) =>
                setForm((f) => ({ ...f, cron_expr: e.target.value }))
              }
              placeholder="0 8 * * 1-5"
              className="font-mono"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Format <code>min hour dom mon dow</code>. Test :{" "}
              <a
                href="https://crontab.guru/"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                crontab.guru
              </a>
            </p>
          </Field>
          <Field label="Fuseau horaire">
            <Input
              value={form.timezone}
              onChange={(e) =>
                setForm((f) => ({ ...f, timezone: e.target.value }))
              }
              placeholder="Europe/Paris"
            />
          </Field>
        </div>

        <Field label="Variables (JSON)">
          <Textarea
            value={form.variables_text}
            onChange={(e) =>
              setForm((f) => ({ ...f, variables_text: e.target.value }))
            }
            rows={6}
            className="font-mono text-xs"
            placeholder='{"scope": "all_at_risk", "inactivity_days": 30}'
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Passé tel quel comme <code>input</code> du skill, après
            interpolation des placeholders <code>{"{{today}}"}</code>,{" "}
            <code>{"{{week_start}}"}</code>, <code>{"{{owner_id}}"}</code>.
          </p>
        </Field>

        <div className="flex items-center gap-2 pt-2">
          <Switch
            id="enabled"
            checked={form.enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
          />
          <Label htmlFor="enabled" className="text-xs cursor-pointer">
            Activée (sinon la routine ne sera pas tickée)
          </Label>
          <div className="flex-1" />
          {error && <span className="text-xs text-destructive">{error}</span>}
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending
              ? "Sauvegarde…"
              : initial
                ? "Mettre à jour"
                : "Créer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RoutineRunsDialog({
  routine,
  onClose,
}: {
  routine: RoutineRow;
  onClose: () => void;
}) {
  const { data: runs = [], isLoading } = useQuery({
    queryKey: ["agentic_routine_runs", routine.id],
    queryFn: () => listRoutineRuns(routine.id),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Historique — {routine.name}</DialogTitle>
          <DialogDescription>
            50 derniers déclenchements. Cliquer sur un identifiant d'
            <code>scheduled_action</code> ouvre le run associé dans l'onglet
            Runs.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Chargement…</div>
        ) : runs.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">
            Aucun déclenchement encore.
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-2 py-1.5">Horaire</th>
                  <th className="text-left px-2 py-1.5">Statut</th>
                  <th className="text-left px-2 py-1.5">action_id</th>
                  <th className="text-left px-2 py-1.5">Erreur</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-2 py-1.5 font-mono">
                      {formatDate(r.fired_at)}
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge
                        variant={
                          r.status === "enqueued" || r.status === "done"
                            ? "secondary"
                            : "destructive"
                        }
                        className="text-[9px]"
                      >
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5 font-mono">
                      {r.scheduled_action_id ?? "—"}
                    </td>
                    <td className="px-2 py-1.5 text-destructive line-clamp-1">
                      {r.error_message ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
